import { Cell, Row } from '@tanstack/react-table';
import { cn } from '../../../../utils/cn';
import React from 'react';
import { TableColumnMeta } from '../Table';
import { isPlainLeftClick } from '../is-plain-left-click';
import { isCellControlClick } from '../card-row';
import { TableCell } from './TableCell';

interface TableRowProps<T> {
  row: Row<T>;
  displayIndex: number;
  to?: string;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLTableRowElement>) => void;
}

const isLinked = <T,>(cell: Cell<T, unknown>) =>
  (cell.column.columnDef.meta as TableColumnMeta | undefined)?.rowLink !==
  false;

export function TableRow<T>({
  row,
  displayIndex,
  to,
  className,
  onClick,
}: TableRowProps<T>) {
  const clickable = Boolean(onClick || to);
  const cells = row.getVisibleCells();
  // One tab stop per row, on the first cell that carries the row link, or on
  // the first cell's link behind its content when every cell opts out.
  const firstLinked = cells.findIndex(isLinked);
  const tabStopIndex = !to ? -1 : firstLinked === -1 ? 0 : firstLinked;

  // A modified or middle click on a linked row opens its route in a new tab.
  // On a row anchor the browser does that itself; elsewhere in the row, such
  // as on a cell's content drawn over its anchor, the row does it, unless a
  // link or control inside the cell owns the click.
  const openInNewTab = (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (
      !to ||
      (event.target as Element).closest('a') ||
      isCellControlClick(event)
    ) {
      return;
    }
    window.open(to, '_blank', 'noopener,noreferrer');
  };

  // Only a plain left click reaches the row's own handler.
  const handleClick =
    onClick || to
      ? (event: React.MouseEvent<HTMLTableRowElement>) => {
          if (to && !isPlainLeftClick(event)) {
            openInNewTab(event);
            return;
          }
          onClick?.(event);
        }
      : undefined;

  const handleAuxClick = to
    ? (event: React.MouseEvent<HTMLTableRowElement>) => {
        if (event.button === 1) {
          openInNewTab(event);
        }
      }
    : undefined;

  return (
    <tr
      role="row"
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      className={cn('group/row', clickable && 'cursor-pointer', className)}
    >
      {cells.map((cell, index) => {
        return (
          <TableCell
            key={cell.id}
            cell={cell}
            displayIndex={displayIndex}
            to={to}
            linked={isLinked(cell)}
            tabbable={index === tabStopIndex}
          />
        );
      })}
    </tr>
  );
}
